import React from 'react';
// hooks
import { useDropdownActions } from 'hooks/useDropdownContext';
// components
import { FormattedMessage } from 'react-intl';
import Link from 'react-router/lib/Link';
import FarmTypeLabel from 'components/FarmTypeLabel/FarmTypeLabel';
import CustomColumn from 'components/DataTable/Columns/CustomColumn';
import ProgressPercent from 'components/ProgressBar/ProgressPercent';
import WaterUsageProgress from 'components/ProgressBar/WaterUsageProgress';
import { GroupStatusBadge } from 'components/StatusBadge/StatusBadge';
import PigGroupMarketingBadge from 'components/PigGroupMarketingBadge';
import Button from 'components/Button';
import VerticalAligner from 'components/VerticalAligner';
import TruncateHint from 'components/TruncateHint';
import FormattedWeight from 'components/FormattedWeight';
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal';
import ABFBadge from 'components/ABFBadge';
import HintTooltip from 'components/HintTooltip';
// utils
import { fetchFromAPI } from 'utils/api';
import { toastResponseErrors } from 'utils/responseErrorsHelper';
import { formatMortality, formatTableValue, showToastrMessage } from 'utils';
import download from 'downloadjs';
// regional date handling
import { formatDateForUser, formatDateForUserShort } from 'utils/regionalDateHelper';
// api
import { flagPigGroup, unflagPigGroup, forceRequestToCloseGroup } from 'endpoints/pigGroups';

export default function useGroupsColumnRenderers(options) {
  const { openDropdown } = useDropdownActions();
  const {
    isAdmin = false,
    isSchedulingAccessOnly,
    isAbfEnabled = false,
    hasFlags = false,
    handleTurnOnEditMode,
    fetchData,
    openModal,
    countryCode = 'US',
  } = options;

  const handleGroupFlagChange = (groupId, isGroupFlagged) => () => {
    if (isGroupFlagged) {
      unflagPigGroup(groupId).then(fetchData).catch(toastResponseErrors);
    } else {
      flagPigGroup(groupId).then(fetchData).catch(toastResponseErrors);
    }
  };

  const handleDownloadCSV = (path, fileName) => (e) => {
    e.stopPropagation();
    return fetchFromAPI(path, { blob: true })
      .then((blob) => download(blob, fileName, 'text/csv'))
      .catch(toastResponseErrors);
  };

  const handleForceClosePigGroup = (groupId) => {
    const handleConfirm = () => {
      forceRequestToCloseGroup(groupId)
        .then(() => {
          fetchData();
          showToastrMessage('component.toastr.group.wasForceCloseRequested');
        })
        .catch(toastResponseErrors);
    };

    openModal(
      <ConfirmationModal
        title={<FormattedMessage id="general.forceCloseGroup" />}
        actionBtnLabel={<FormattedMessage id="component.modal.forceCloseGroup.confirm" />}
        actionBtnProps={{ blue: true }}
        warningMessage={<FormattedMessage id="component.modal.forceCloseGroup.text" />}
        handleConfirm={handleConfirm}
      />
    );
  };

  const handleOpenDropdown = (e, pigGroup, isSmallScreenView) => {
    const { flagged, id, name, external_source_id, status, pigs } = pigGroup;
    const csvPath = `/csv/barnsheets/pig_groups/${id}`;

    const generalOptions = [
      {
        label: flagged ?
          <FormattedMessage id="container.farmfeed.row.unflag" /> :
          <FormattedMessage id="container.farmfeed.row.flag" />,
        onClick: handleGroupFlagChange(id, flagged),
        hide: !hasFlags,
      },
      {
        label: <FormattedMessage id="general.downloadBarnSheet" />,
        onClick: handleDownloadCSV(csvPath, `EveryPig_Barnsheets_${name}.csv`),
        hide: isSchedulingAccessOnly,
      },
      {
        label: <FormattedMessage id="general.downloadMortalityData" />,
        onClick: handleDownloadCSV(`${csvPath}/mortalities`, `EveryPig_Mortality_Report_${name}.csv`),
        hide: isSchedulingAccessOnly,
      },
      {
        label: <FormattedMessage id="general.downloadTreatmentData" />,
        onClick: handleDownloadCSV(`${csvPath}/treatment_products`, `EveryPig_Treatments_${name}.csv`),
        hide: isSchedulingAccessOnly,
      },
      {
        label: <FormattedMessage id="general.downloadSymptomData" />,
        onClick: handleDownloadCSV(`${csvPath}/symptoms`, `EveryPig_Symptoms_${name}.csv`),
        hide: isSchedulingAccessOnly,
      },
      {
        label: <FormattedMessage id="general.downloadDiagnosesData" />,
        onClick: handleDownloadCSV(`${csvPath}/diagnoses`, `EveryPig_Diagnoses_${name}.csv`),
        hide: isSchedulingAccessOnly,
      },
      {
        label: <FormattedMessage id="general.downloadMovementData" />,
        onClick: handleDownloadCSV(`${csvPath}/pig_migrations`, `EveryPig_Pig_Migrations_${name}.csv`),
        hide: isSchedulingAccessOnly,
      },
      {
        label: <FormattedMessage id="general.downloadSaleMovements" />,
        onClick: handleDownloadCSV(`${csvPath}/sale_pig_migrations`, `EveryPig_Sale_Pig_Migrations_${name}.csv`),
        hide: isSchedulingAccessOnly,
      },
      { label: <FormattedMessage id="general.manageGroup" />, url: `/groups/${id}`, hide: !isAdmin },
      {
        label: <FormattedMessage id="general.forceCloseGroup" />,
        hide: !pigs || status !== 'opened',
        onClick: () => handleForceClosePigGroup(id),
      },
      {
        label: <FormattedMessage id="general.viewSource" />, url: `/sources/${external_source_id}`,
        hide: !isAdmin || !external_source_id
      },
    ];

    const additionalOptions = [
      { label: <FormattedMessage id="general.button.view" />, url: `/barnsheets/groups/${id}` },
      { label: <FormattedMessage id="general.editData" />, onClick: () => handleTurnOnEditMode?.(id) },
    ];

    const options = isSmallScreenView ? generalOptions : additionalOptions.concat(generalOptions);
    openDropdown(e, options, { isScrollIgnored: true });
  };

  const renderGroupNameColumn = (rowData, { rowIndex }) => {
    const { id, name, flagged } = rowData;
    return (
      <CustomColumn>
        <div className="group-id-column">
          <VerticalAligner>
            <span className="hide-for-large semibold">
              <FormattedMessage id="component.dataTable.headers.groupId" />&nbsp;•&nbsp;
            </span>
            <Link to={`/barnsheets/groups/${id}`} className="color-primary">
              {name}
            </Link>
            <PigGroupMarketingBadge
              className="ml-5"
              size="small"
              pigGroup={rowData}
              isMinimized
              isHintAttachedToBody
            />
            {flagged && hasFlags && (
              <TruncateHint showName={<i className="fa fa-bookmark-bts primary ml-10" />}>
                <FormattedMessage id="general.flagged" />
              </TruncateHint>
            )}
          </VerticalAligner>
          <i
            data-name={`${name}-${id}-${rowIndex}`}
            className="fa fa-dots-three-horizontal hide-for-large"
            onClick={(e) => handleOpenDropdown(e, rowData, true)}
          />
        </div>
      </CustomColumn>
    );
  };

  const renderFarmNameColumn = (rowData) => (
    <CustomColumn label={<FormattedMessage id="component.dataTable.headers.farmName" />}>
      <Link className="primary-link" to={`/barnsheets/farms/${rowData.farm_id}/groups`}>
        <TruncateHint name={rowData.farm_name} />
      </Link>
    </CustomColumn>
  );

  const renderFarmTypeColumn = (rowData) => (
    <CustomColumn label={<FormattedMessage id="general.type" />}>
      <FarmTypeLabel farmType={rowData.farm_type} />
    </CustomColumn>
  );

  const renderTreatedColumn = (rowData) => {
    const { avg_treated_24h } = rowData;
    return (
      <CustomColumn textRight label={<FormattedMessage id="general.treatedPeriod" values={{ period: '24h' }} />}>
        {formatTableValue(avg_treated_24h, Number(avg_treated_24h).toFixed(2) + '%', '-')}
      </CustomColumn>
    );
  };

  const renderInventoryColumn = (rowData) => (
    <CustomColumn textRight label={<FormattedMessage id="component.dataTable.headers.inventory" />}>
      {formatTableValue(rowData.pigs)}
    </CustomColumn>
  );

  const renderHeadTreatedPigsColumn = (rowData) => (
    <CustomColumn textRight label={<FormattedMessage id="general.headTreated" />}>
      {formatTableValue(rowData.treated_pigs)}
    </CustomColumn>
  );

  const renderEstimatedWeightColumn = (rowData) => (
    <CustomColumn textRight label={<FormattedMessage id="general.estAvgWt" />}>
      <FormattedWeight value={rowData.estimated_weight} />
    </CustomColumn>
  );

  const renderMortalityRateColumn = (rowData) => (
    <CustomColumn textRight label={<FormattedMessage id="component.dataTable.headers.mortRate" />}>
      {formatMortality(rowData.mortality_rate)}
    </CustomColumn>
  );

  const renderTotalMortalityColumn = (rowData) => {
    const {
      acute_death_count,
      chronic_death_count,
      euthanasia_death_count,
      treated_death_count,
      acute_deaths,
      chronic_deaths,
      euthanasia_deaths,
      treated_deaths,
      total_deaths,
    } = rowData || {};

    const acute = acute_death_count ?? acute_deaths ?? 0;
    const chronic = chronic_death_count ?? chronic_deaths ?? 0;
    const euthanasia = euthanasia_death_count ?? euthanasia_deaths ?? 0;
    const treated = treated_death_count ?? treated_deaths ?? 0;

    return (
      <CustomColumn textRight label={<FormattedMessage id="component.dataTable.headers.totalMortality" />}>
        {total_deaths !== null && total_deaths !== undefined ? (
          <span className="position-relative">
            {total_deaths}
            <HintTooltip attachToBody showBelow arrowed>
              <div className="bs-mortality-tooltip">
                <div><FormattedMessage id="general.deaths.acute" />: {acute}</div>
                <div><FormattedMessage id="general.deaths.chronic" />: {chronic}</div>
                <div><FormattedMessage id="general.deaths.euthanasia" />: {euthanasia}</div>
                {isAbfEnabled && (
                  <div><FormattedMessage id="general.treated" />: {treated}</div>
                )}
              </div>
            </HintTooltip>
          </span>
        ) : 'N/A'}
      </CustomColumn>
    );
  };

  const renderProgressColumn = (rowData) => (
    <CustomColumn textRight label={<FormattedMessage id="component.dataTable.headers.compliance" />}>
      {formatTableValue(rowData.progress, <ProgressPercent progress={rowData.progress} />)}
    </CustomColumn>
  );

  const renderWaterUsageDiffColumn = (rowData) => {
    const { water_usage_diff_percentage } = rowData;
    return (
      <CustomColumn textRight label={<FormattedMessage id="component.dataTable.headers.waterUsage" />}>
        <WaterUsageProgress progress={water_usage_diff_percentage} />
      </CustomColumn>
    );
  };

  const renderPigsInColumn = (rowData) => (
    <CustomColumn textRight label={<FormattedMessage id="general.pigsIn" />}>
      {formatTableValue(rowData.total_pigs_in)}
    </CustomColumn>
  );

  const renderStartDateColumn = (rowData) => (
    <CustomColumn textRight label={<FormattedMessage id="component.dataTable.headers.startDate" />}>
      {formatTableValue(rowData.started_on, formatDateForUser(rowData.started_on, countryCode))}
    </CustomColumn>
  );

  const renderCloseDateColumn = (rowData) => (
    <CustomColumn textRight label={<FormattedMessage id="component.dataTable.headers.closeDate" />}>
      {formatTableValue(rowData.closed_on, formatDateForUser(rowData.closed_on, countryCode))}
    </CustomColumn>
  );

  const renderDaysOnColumn = (rowData) => (
    <CustomColumn textRight label={<FormattedMessage id="general.daysOnFeed" />}>
      {formatTableValue(rowData.days_on_feed)}
    </CustomColumn>
  );

  const renderStatusColumn = (rowData) => (
    <CustomColumn label={<FormattedMessage id="component.dataTable.headers.status" />} noBottomBorder>
      <GroupStatusBadge
        status={rowData.status}
        textValues={{
          date: rowData.closed_on ? formatDateForUserShort(rowData.closed_on, countryCode) : null,
        }}
      />
    </CustomColumn>
  );

  const renderStatusWithoutDateColumn = (rowData) => (
    <CustomColumn label={<FormattedMessage id="component.dataTable.headers.status" />} noBottomBorder>
      <GroupStatusBadge status={rowData.status} textValues={{ date: null }} />
    </CustomColumn>
  );

  const renderDaysOnFeed = (rowData) => (
    <CustomColumn textRight label={<FormattedMessage id="general.daysOnFeed" />}>
      {formatTableValue(rowData.days_on_feed)}
    </CustomColumn>
  );

  const renderButtonsColumn = (rowData, { rowIndex }) => {
    const { name, id } = rowData;
    return (
      <div className="collapsible-value button-column">
        <i
          data-name={`${name}-${id}-${rowIndex}`}
          className="fa fa-dots-three-horizontal show-for-large"
          onClick={(e) => handleOpenDropdown(e, rowData)}
        />
        <Link to={`/barnsheets/groups/${id}`} className="button light hide-for-large">
          <FormattedMessage id="general.button.view" />
        </Link>
        <Button darkGrey onClick={() => handleTurnOnEditMode(id)} className="hide-for-large">
          <FormattedMessage id="general.editData" />
        </Button>
      </div>
    );
  };

  const renderAbfStatusColumn = (rowData) => {
    const { abf_tracking } = rowData;
    return (
      <CustomColumn label={<FormattedMessage id="general.abfStatus" />}>
        {abf_tracking ? <ABFBadge /> : 'N/A'}
      </CustomColumn>
    );
  };

  const renderHealthStatusColumn = (rowData) => {
    const names = rowData.pig_group_diagnoses_names;
    const hasNames = Array.isArray(names) && names.length;
    return (
      <CustomColumn label={<FormattedMessage id="general.healthStatus" />}>
        {hasNames ? <TruncateHint name={names.join(', ')} /> : '—'}
      </CustomColumn>
    );
  };

  const renderExpandable = (rowData) => {
    const { id, name } = rowData;
    const csvPath = `/csv/barnsheets/pig_groups/${id}`;
    return (
      <div>
        {isAdmin && (
          <Link to={`/groups/${id}`} className="button small light">
            <i className="fa fa-gear mr-5" />
            <FormattedMessage id="general.manage" />
          </Link>
        )}
        <Button small light onClick={() => handleTurnOnEditMode(id)}>
          <i className="fa fa-pencil mr-5" />
          <FormattedMessage id="general.button.edit" />
        </Button>
        <Link to={`/barnsheets/groups/${id}`} className="button small light">
          <i className="fa fa-eye mr-5" />
          <FormattedMessage id="general.viewBarnSheet" />
        </Link>
        <Button
          small
          light
          onClick={handleDownloadCSV(csvPath, `EveryPig_Barnsheets_${name}.csv`)}
        >
          <i className="fa fa-download-btn mr-5" />
          <FormattedMessage id="general.pageTitle.barnsheets" />
        </Button>
        <Button
          small
          light
          onClick={handleDownloadCSV(
            `${csvPath}/mortalities`,
            `EveryPig_Mortality_Report_${name}.csv`
          )}
        >
          <i className="fa fa-download-btn mr-5" />
          <FormattedMessage id="general.pageTitle.mortalities" />
        </Button>
        <Button
          small
          light
          onClick={handleDownloadCSV(
            `${csvPath}/treatment_products`,
            `EveryPig_Treatments_${name}.csv`
          )}
        >
          <i className="fa fa-download-btn mr-5" />
          <FormattedMessage id="general.treatments" />
        </Button>
        <Button
          small
          light
          onClick={handleDownloadCSV(`${csvPath}/symptoms`, `EveryPig_Symptoms_${name}.csv`)}
        >
          <i className="fa fa-download-btn mr-5" />
          <FormattedMessage id="component.analyticsPanel.title.symptoms" />
        </Button>
        <Button
          small
          light
          onClick={handleDownloadCSV(
            `${csvPath}/pig_migrations`,
            `EveryPig_Pig_Migrations_${name}.csv`
          )}
        >
          <i className="fa fa-download-btn mr-5" />
          <FormattedMessage id="general.pigMigrations" />
        </Button>
      </div>
    );
  };

  return {
    renderButtonsColumn,
    renderDaysOnColumn,
    renderGroupNameColumn,
    renderEstimatedWeightColumn,
    renderExpandable,
    renderWaterUsageDiffColumn,
    renderFarmNameColumn,
    renderFarmTypeColumn,
    renderInventoryColumn,
    renderMortalityRateColumn,
    renderTotalMortalityColumn,
    renderPigsInColumn,
    renderProgressColumn,
    renderTreatedColumn,
    renderStartDateColumn,
    renderCloseDateColumn,
    renderStatusColumn,
    renderStatusWithoutDateColumn,
    renderDaysOnFeed,
    renderAbfStatusColumn,
    renderHealthStatusColumn,
    renderHeadTreatedPigsColumn,
  };
}
